import { useMemo } from 'react'
import {
  ALTERNATIVE_TINT,
  DARK_SQUARE,
  LIGHT_SQUARE,
  PLAYED_TINT,
  arrow,
  boardRows,
  parseFen,
  squaresOf,
  type Marks,
} from '@/lib/board'
import { cn } from '@/lib/utils'

import bB from '../assets/pieces/bB.svg'
import bK from '../assets/pieces/bK.svg'
import bN from '../assets/pieces/bN.svg'
import bP from '../assets/pieces/bP.svg'
import bQ from '../assets/pieces/bQ.svg'
import bR from '../assets/pieces/bR.svg'
import wB from '../assets/pieces/wB.svg'
import wK from '../assets/pieces/wK.svg'
import wN from '../assets/pieces/wN.svg'
import wP from '../assets/pieces/wP.svg'
import wQ from '../assets/pieces/wQ.svg'
import wR from '../assets/pieces/wR.svg'

/**
 * Cburnett, the set the report already draws with.
 *
 * Vendored under `data/vendor/pieces/cburnett` for the PDF and copied here for
 * the bundler; `scripts/ci.sh` fails if the two copies drift. Unicode glyphs
 * were tried first and looked exactly as bad as they sound.
 *
 * Twelve plain imports rather than `import.meta.glob`: the glob resolved to
 * nothing under the test runner, which drew twelve empty squares while every
 * assertion about the markup still passed.
 */
const PIECES: Record<string, string> = {
  wK, wQ, wR, wB, wN, wP,
  bK, bQ, bR, bB, bN, bP,
}

export type ChessboardProps = {
  fen: string
  /** UCI of the move that was played; drawn in green. */
  playedUci?: string
  /** UCI of the alternative; drawn in amber, under the played arrow. */
  alternativeUci?: string
  className?: string
}

/**
 * The diagram, with both candidate moves on it.
 *
 * The report can only print one position per board and describes the two moves
 * in words; here they are both on the board at once, which is the single thing
 * the screen can do that the paper cannot. Green sits on top of amber because
 * the played move is the subject and the alternative is the contrast.
 */
export function Chessboard({
  fen,
  playedUci,
  alternativeUci,
  className,
}: ChessboardProps) {
  const { rows, flipped, arrows } = useMemo(() => {
    const { turn } = parseFen(fen)
    const flip = turn === 'b'
    const marks: Marks = {}
    if (alternativeUci) {
      for (const square of squaresOf(alternativeUci)) marks[square] = 'alternative'
    }
    if (playedUci) {
      for (const square of squaresOf(playedUci)) marks[square] = 'played'
    }
    return {
      rows: boardRows(fen, flip, marks),
      flipped: flip,
      arrows: {
        played: playedUci ? arrow(playedUci, flip) : null,
        alternative: alternativeUci ? arrow(alternativeUci, flip) : null,
      },
    }
  }, [fen, playedUci, alternativeUci])

  return (
    <div
      className={cn('relative overflow-hidden rounded-md', className)}
      style={{ containerType: 'inline-size' }}
    >
      {rows.map((row) => (
        <div key={row[0]?.name} className="grid grid-cols-8">
          {row.map((square) => (
            <div
              key={square.name}
              className="relative aspect-square"
              style={{ background: square.light ? LIGHT_SQUARE : DARK_SQUARE }}
            >
              {square.mark ? (
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      square.mark === 'played' ? PLAYED_TINT : ALTERNATIVE_TINT,
                  }}
                />
              ) : null}
              {square.piece ? (
                <div
                  className="absolute inset-0 bg-contain bg-center bg-no-repeat"
                  // Quoted: the bundler inlines the piece as a data URI whose
                  // markup carries apostrophes, and an unquoted url() token may
                  // not contain a quote. Without these the CSS value is invalid
                  // and the board renders as sixty-four empty squares.
                  style={{ backgroundImage: `url("${PIECES[square.piece]}")` }}
                  data-piece={square.piece}
                />
              ) : null}
              {square.rankLabel ? (
                <span
                  className="pointer-events-none absolute right-[4px] top-[1px] font-bold"
                  style={{
                    fontSize: '2.05cqw',
                    color: square.light ? DARK_SQUARE : LIGHT_SQUARE,
                  }}
                >
                  {square.rankLabel}
                </span>
              ) : null}
              {square.fileLabel ? (
                <span
                  className="pointer-events-none absolute bottom-0 left-[4px] font-bold"
                  style={{
                    fontSize: '2.05cqw',
                    color: square.light ? DARK_SQUARE : LIGHT_SQUARE,
                  }}
                >
                  {square.fileLabel}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ))}

      <svg
        viewBox="0 0 800 800"
        className="pointer-events-none absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        {arrows.alternative ? (
          <g opacity="0.82">
            <line
              x1={arrows.alternative.x1}
              y1={arrows.alternative.y1}
              x2={arrows.alternative.x2}
              y2={arrows.alternative.y2}
              stroke="#C08419"
              strokeWidth="15"
              strokeLinecap="round"
            />
            <polygon points={arrows.alternative.head} fill="#C08419" />
          </g>
        ) : null}
        {arrows.played ? (
          <g opacity="0.92">
            <line
              x1={arrows.played.x1}
              y1={arrows.played.y1}
              x2={arrows.played.x2}
              y2={arrows.played.y2}
              stroke="#1F7A46"
              strokeWidth="15"
              strokeLinecap="round"
            />
            <polygon points={arrows.played.head} fill="#1F7A46" />
          </g>
        ) : null}
      </svg>

      <span className="sr-only">
        {flipped ? 'Na posunięciu: czarne' : 'Na posunięciu: białe'}
      </span>
    </div>
  )
}
